import type { NudgeId } from "./nudge.types";
import { NUDGE_COOLDOWN_MS, NUDGE_PRIORITY } from "./nudge.types";

const SESSION_KEY = "podhive:nudge-session-shown";
const GLOBAL_COOLDOWN_KEY = "podhive:nudge-global-cooldown";
const CREATE_FLOW_COUNT_PREFIX = "podhive:create-flow-count:";

function impressionKey(userId: string, nudgeId: NudgeId): string {
	return `podhive:nudge-impression:${userId}:${nudgeId}`;
}

function dismissedKey(userId: string, nudgeId: NudgeId): string {
	return `podhive:nudge-dismissed:${userId}:${nudgeId}`;
}

/**
 * Persistence helpers for upgrade-nudge pacing.
 * Session = one nudge max per browser tab session.
 * Global cooldown = 48h between any two nudges for a user.
 * Per-nudge dismiss = never re-show that specific nudge once declined.
 */
export const NudgeStore = {
	canShow(userId: string, nudgeId: NudgeId): boolean {
		if (typeof window === "undefined") return false;

		// One nudge per session.
		const sessionShown = sessionStorage.getItem(SESSION_KEY);
		if (sessionShown && sessionShown !== nudgeId) return false;

		// Already dismissed this nudge permanently (or until they convert).
		if (localStorage.getItem(dismissedKey(userId, nudgeId))) return false;

		// Already shown this nudge once (impression cap of 1 unless reset).
		if (localStorage.getItem(impressionKey(userId, nudgeId))) return false;

		// Global cooldown across nudges.
		const cooldownUntil = Number(
			localStorage.getItem(GLOBAL_COOLDOWN_KEY) ?? "0",
		);
		if (cooldownUntil > Date.now()) {
			// Allow the same session-claimed nudge to still render after a remount.
			if (sessionShown === nudgeId) return true;
			return false;
		}

		// If another higher-priority nudge claimed the session, block.
		if (sessionShown) {
			const claimedPriority = NUDGE_PRIORITY[sessionShown as NudgeId] ?? 0;
			if (claimedPriority >= NUDGE_PRIORITY[nudgeId]) return false;
		}

		return true;
	},

	markShown(userId: string, nudgeId: NudgeId): void {
		sessionStorage.setItem(SESSION_KEY, nudgeId);
		localStorage.setItem(impressionKey(userId, nudgeId), "1");
		localStorage.setItem(
			GLOBAL_COOLDOWN_KEY,
			String(Date.now() + NUDGE_COOLDOWN_MS),
		);
	},

	markDismissed(userId: string, nudgeId: NudgeId): void {
		localStorage.setItem(dismissedKey(userId, nudgeId), "1");
		NudgeStore.markShown(userId, nudgeId);
	},

	/** Increment and return how many times this user opened the create flow. */
	incrementCreateFlowCount(userId: string): number {
		const key = `${CREATE_FLOW_COUNT_PREFIX}${userId}`;
		const next = Number(localStorage.getItem(key) ?? "0") + 1;
		localStorage.setItem(key, String(next));
		return next;
	},

	getCreateFlowCount(userId: string): number {
		return Number(
			localStorage.getItem(`${CREATE_FLOW_COUNT_PREFIX}${userId}`) ?? "0",
		);
	},
};
